import React, { useCallback, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { chartColors, sensorInterval, themeColors } from '../../../constants';
import {
  makeStyles,
  Theme,
  createStyles,
  Typography,
} from '@material-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IReduxState, ISampledTelemetry, IChartProps } from '../../../types';
import { IPoint } from '../../../types';
import { useWindowSize } from '../../useWindowSize';
import { ActivePointLayer } from '../../ActivePointLayer';
import { CardLayoutGraph } from '../CardLayouts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tooltipWrapper: {
      position: 'absolute',
      background: themeColors.white,
      opacity: 0.9,
      padding: theme.spacing(1),
      color: theme.palette.secondary.main,
      zIndex: 100,
      top: 20,
      left: -60,
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2),
        paddingRight: 0,
        width: 160,
        top: 20,
        left: -70,
      },
    },
    tooltipLabel: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    center: {
      display: 'flex',
      height: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      position: 'absolute',
      width: '100%',
      top: theme.spacing(2),
    },
  })
);

export default function FuelConsumptionLine(props: IChartProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const size = useWindowSize();

  let isSmallScreen = size.width !== undefined && size.width < 500;
  const { isPdfReport } = props;
  // Set isSmallScreen to false for the charts used in the PDF report
  isSmallScreen = isPdfReport === true ? false : isSmallScreen;

  const graphMargin = { top: 50, right: 80, bottom: 100, left: 50 };
  const graphMarginMobile = { top: 25, right: 25, bottom: 150, left: 50 };

  const isHybrid = useSelector(
    (state: IReduxState) =>
      // !!state.machineDetails.machine.telemetrySnapshot?.isHybrid
      !!state.machineDetails.machine.isHybrid
  );
  const telemetry = useSelector(
    (state: IReduxState) => state.machineDetails.telemetry
  );
  const telemetryData: ISampledTelemetry | any = !!telemetry ? telemetry : [];
  const graphDateFormat = useSelector(
    (state: IReduxState) => state.machineDetails.graphDateFormat
  );

  // animate point on hover
  const [current, setCurrent] = useState(null);
  const handleHover = useCallback(
    (point) => {
      setCurrent(point);
    },
    [setCurrent]
  );

  const filteredEngineFuelRateData = telemetryData.filter(
    (item: ISampledTelemetry) => item.engineFuelRate !== null
  );

  const engineFuelRateUnit = filteredEngineFuelRateData.length
    ? filteredEngineFuelRateData[0].engineFuelRate?.unit
    : t('graph.l/t');

  const filteredMainDischargeConveyorFuelRateData = telemetryData.filter(
    (item: ISampledTelemetry) => item.mainDischargeConveyorFuelRate !== null
  );

  const mainDischargeConveyorFuelRateUnit = filteredMainDischargeConveyorFuelRateData.length
    ? filteredMainDischargeConveyorFuelRateData[0].mainDischargeConveyorFuelRate
        ?.unit
    : t('graph.l/t');

  const filteredSideDischargeConveyorFuelRateData = telemetryData.filter(
    (item: ISampledTelemetry) => item.sideDischargeConveyorFuelRate !== null
  );
  const sideDischargeConveyorFuelRateUnit = filteredSideDischargeConveyorFuelRateData.length
    ? filteredSideDischargeConveyorFuelRateData[0].sideDischargeConveyorFuelRate
        ?.unit
    : t('graph.l/t');

  const chartData = [
    {
      id: `${t('graph.fuelConsumption')} [${engineFuelRateUnit}]`,
      color: chartColors.purple,
      data: telemetryData
        .filter(
          (item: ISampledTelemetry, index: number) =>
            (!item.isEmpty && item.engineFuelRate !== null) ||
            (item.durationInSeconds &&
              item.durationInSeconds > sensorInterval.engineFuelRate) ||
            // TODO KANM remove when updating NIVO and add min/max to xScale
            // enable first and last items
            index === 0 ||
            index === telemetryData.length - 1
        )
        .map((item: ISampledTelemetry) => {
          return {
            x: moment(item.timestamp)
              .utc()
              .local()
              .format('YYYY-MM-DD HH:mm:ss.SSS'),
            y:
              item.engineFuelRate !== null && item.engineFuelRate?.value
                ? Math.max(item.engineFuelRate.value, 0.01).toFixed(2)
                : null,
          };
        }),
    },
    {
      id: `${t(
        'graph.mainDischargeConveyorShort'
      )} [${mainDischargeConveyorFuelRateUnit}]`,
      color: chartColors.cyan,
      data: telemetryData
        .filter(
          (item: ISampledTelemetry) =>
            (!item.isEmpty && item.mainDischargeConveyorFuelRate !== null) ||
            (item.durationInSeconds &&
              item.durationInSeconds > sensorInterval.engineFuelRate)
        )
        .map((item: ISampledTelemetry) => {
          return {
            x: moment(item.timestamp)
              .utc()
              .local()
              .format('YYYY-MM-DD HH:mm:ss.SSS'),
            y:
              item.mainDischargeConveyorFuelRate !== null &&
              item.mainDischargeConveyorFuelRate?.value
                ? Math.max(
                    item.mainDischargeConveyorFuelRate.value,
                    0.01
                  ).toFixed(2)
                : null,
          };
        }),
    },
    {
      id: `${t(
        'graph.secondDischargeConveyorShort'
      )} [${sideDischargeConveyorFuelRateUnit}]`,
      color: chartColors.green,
      data: telemetryData
        .filter(
          (item: ISampledTelemetry, index: number) =>
            (!item.isEmpty && item.sideDischargeConveyorFuelRate !== null) ||
            (item.durationInSeconds &&
              item.durationInSeconds > sensorInterval.engineFuelRate) ||
            // TODO KANM remove when updating NIVO and add min/max to xScale
            // enable first and last items
            index === 0 ||
            index === telemetryData.length - 1
        )
        .map((item: ISampledTelemetry) => {
          return {
            x: moment(item.timestamp)
              .utc()
              .local()
              .format('YYYY-MM-DD HH:mm:ss.SSS'),
            y:
              item.sideDischargeConveyorFuelRate !== null &&
              item.sideDischargeConveyorFuelRate?.value
                ? Math.max(
                    item.sideDischargeConveyorFuelRate.value,
                    0.01
                  ).toFixed(2)
                : null,
          };
        }),
    },
  ];

  const CustomTooltip = (pointData: IPoint) => {
    const { data, serieId, serieColor, x, y } = pointData.point;
    const unit = serieId.toString().startsWith(t('graph.fuelConsumption'))
      ? engineFuelRateUnit
      : mainDischargeConveyorFuelRateUnit;

    // Dynamische Ausrichtung abhängig von X/Y-Position
    const tooltipLeft = x < 120 ? 20 : -180;
    const tooltipTop = y > 300 ? -160 : 20;
    // console.log("tooltipTop : " + tooltipTop);

    return (
      <div className={classes.tooltipWrapper} style={{ top: tooltipTop, left: tooltipLeft }}>
        <div className={classes.tooltipLabel}>
          <div style={{ background: `${serieColor}`, width: 10, height: 10 }} />
          <span style={{ color: `${serieColor}`, marginLeft: 8 }}>
            {serieId}
          </span>
        </div>
        <div>
          <span style={{ color: `${serieColor}` }}>{t('graph.date')}: </span>
          <span>{moment(data.x).format('DD.MM.YYYY')}</span>
        </div>
        <div>
          <span style={{ color: `${serieColor}` }}>{t('graph.time')}: </span>
          <span>{moment(data.x).format('HH:mm:ss')}</span>
        </div>
        <div>
          <span style={{ color: `${serieColor}` }}>{t('graph.value')}: </span>
          <span>
            {data.y} {unit}
          </span>
        </div>
      </div>
    );
  };

  return (
    <CardLayoutGraph
      cardStyle={{
        boxShadow:
          isHybrid && !isPdfReport
            ? `0.25rem 0.25rem 0 0 ${chartColors.electricBlue}`
            : 'none',
      }}
    >
      {telemetryData.length === 0 ||
      (telemetryData.engineFuelRate === null &&
        telemetryData.mainDischargeConveyorFuelRate === null &&
        telemetryData.sideDischargeConveyorFuelRate === null) ? (
        <div className={classes.center}>
          <Typography align="center" variant="h4">
            {t('graph.fuelConsumption')}
          </Typography>

          <Typography align="center" variant="h5">
            {t('graph.noData')}
          </Typography>
        </div>
      ) : (
        <React.Fragment>
          {!isSmallScreen && (
            <div className={classes.title}>
              <Typography align="center" variant="h6">
                {t('graph.fuelConsumption')}
              </Typography>
            </div>
          )}
          
          <ResponsiveLine
            theme={{
              tooltip: {
                container: {
                  background: '#2d374d',
                  color: 'inherit',
                  boxShadow: '0 3px 9px rgba(0, 0, 0, 0.5)',
                },
              },
              axis: {
                legend: {
                  text: {
                    fill: '#fff',
                  },
                },
                ticks: {
                  line: {
                    stroke: 'gray',
                  },
                  text: {
                    fill: '#fff',
                  },
                },
              },
            }}
            data={chartData.reverse()}
            animate={false}
            colors={(d: any) => d.color}
            lineWidth={2}
            margin={isSmallScreen ? graphMarginMobile : graphMargin}
            xScale={{
              type: 'time',
              format: '%Y-%m-%d %H:%M:%S.%L',
              precision: 'millisecond',
              useUTC: false,
            }}
            xFormat="time:%Y-%m-%d %H:%M:%S.%L"
            gridYValues={[0.01, 0.05, 0.1, 0.5, 1, 5, 10, 50, 100]}
            yScale={{ type: 'log', min: 0.01, max: 100, base: 10 }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            enablePoints={true}
            layers={[
              'grid',
              'markers',
              'axes',
              'areas',
              'lines',
              'slices',
              'points',
              'mesh',
              'legends',
              () => <ActivePointLayer point={current} />,
            ]}
            //@ts-ignore
            axisBottom={{
              orient: 'bottom',
              tickSize: 10,
              tickPadding: 40,
              tickRotation: 270,
              legendPosition: 'middle',
              format: (tick) => moment(tick).format(graphDateFormat),
              // tickValues: graphTickValues,
            }}
            tooltip={CustomTooltip}
            onMouseEnter={handleHover}
            onMouseMove={handleHover}
            onMouseLeave={handleHover}
            axisLeft={{
              orient: 'left',
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              tickValues: [0.01, 0.05, 0.1, 0.5, 1, 5, 10, 50, 100],
              legend: `${t('graph.fuelConsumption')}`,
              legendOffset: -40,
              legendPosition: 'middle',
            }}
            enableGridX={false}
            enableCrosshair={false}
            pointSize={2}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabel="x"
            pointLabelYOffset={-12}
            useMesh={true}
            legends={[
              {
                anchor: isSmallScreen ? 'bottom-right' : 'bottom',
                direction: isSmallScreen ? 'column' : 'row',
                justify: false,
                translateX: 0,
                translateY: isSmallScreen ? 140 : 85,
                itemsSpacing: isSmallScreen ? 0 : 10,
                itemDirection: 'left-to-right',
                itemWidth: isSmallScreen ? 200 : 200,
                itemHeight: 20,
                itemOpacity: 0.75,
                itemTextColor: '#fff',
                symbolSize: 12,
                symbolShape: 'circle',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .03)',
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]}
          />
        </React.Fragment>
      )}
    </CardLayoutGraph>
  );
}
